import React, { useRef, useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  IconButton,
  Stack,
  useTheme,
  useMediaQuery
} from '@mui/material';

// Iconos
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { PhotoCamera, Mic, VideoCameraBack, Settings } from '@mui/icons-material';

import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';


const CreacionContenidoIA = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const scrollContainerRef = useRef(null); 
  
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  // ---------------------------------------------
  // DATOS: Herramientas de Contenido IA
  // ---------------------------------------------
  const iaContentTools = [
    { 
      id: 1,
      title: 'Generación de Imagen',
      label: 'Visual',
      icon: PhotoCamera, 
      actionText: 'Generar',
      background: 'url(/images/creaciodeimagenia.jpg)'
    },
    { 
      id: 2,
      title: 'Voz en Off Hiperrealista',
      label: 'Audio',
      icon: Mic,
      actionText: 'Probar',
      background: 'url(/images/vozenoff.jpg)'
    },
    { 
      id: 3,
      title: 'Edición de Video Ágil',
      label: 'Video',
      icon: VideoCameraBack,
      actionText: 'Crear',
      background: 'url(/images/videoia.png)'
    },
    { 
      id: 5,
      title: 'Gestión de RRSS',
      label: 'Automatización',
      icon: Settings,
      actionText: 'Programar',
      background: 'url(/images/gestionderedes.jpg)',
    },
  ];

  // ---------------------------------------------
  // TARJETA DE CONTENIDO HORIZONTAL
  // ---------------------------------------------
  const ContentCard = ({ title, label, actionText, background, isSpecial = false, icon: Icon }) => (
    <Box
      sx={{
        minWidth: isMobile ? 280 : 320, 
        height: 170, 
        flexShrink: 0, 
        borderRadius: 3, // Unificado a 3 como los anteriores
        overflow: 'hidden',
        position: 'relative',
        backgroundImage: background,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        boxShadow: 2,
        cursor: 'pointer',
        transition: '0.25s ease',
        '&:hover': {
          transform: isMobile ? 'none' : 'scale(1.02)',
        }
      }}
    >
      <Stack
        justifyContent="space-between"
        alignItems="flex-start"
        sx={{
          p: 2,
          height: '100%',
          width: '100%',
          position: 'relative',
          background: 'linear-gradient(to right, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 50%, rgba(0,0,0,0) 100%)',
        }}
      >
        <Stack spacing={1} sx={{ maxWidth: '70%' }}>
          <Box sx={{
            bgcolor: 'rgba(255, 255, 255, 0.2)',
            backdropFilter: 'blur(4px)',
            borderRadius: 1,
            p: '3px 8px',
            width: 'fit-content',
          }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'white', textTransform: 'uppercase', fontSize: 10 }}>
              {label}
            </Typography>
          </Box>

          <Typography variant="h6" sx={{ fontWeight: 800, color: 'white', lineHeight: 1.2 }}>
              {title}
          </Typography>

          <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
            <Box
              sx={{
                width: 28, height: 28,
                borderRadius: '50%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: 'primary.main',
                color: 'white'
              }}
            >
              <PlayArrowIcon sx={{ fontSize: 16 }} />
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'white' }}>
              {actionText}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );

  // ---------------------------------------------
  // LÓGICA DE SCROLL UNIFICADA
  // ---------------------------------------------
  const checkScroll = () => {
    const container = scrollContainerRef.current;
    if (container) {
      setShowLeftArrow(container.scrollLeft > 10);
      setShowRightArrow(container.scrollLeft < container.scrollWidth - container.clientWidth - 10);
    }
  };

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -350 : 350,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      checkScroll();
      container.addEventListener('scroll', checkScroll);
      window.addEventListener('resize', checkScroll);
    }
    return () => {
      if (container) {
        container.removeEventListener('scroll', checkScroll);
        window.removeEventListener('resize', checkScroll);
      }
    };
  }, []);

  return (
    <Box sx={{ width: '100%', mt: 4 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#211E26', ml: 1 }}>
        Herramientas de Contenido IA
      </Typography>

      <Box sx={{ position: 'relative' }}>
        {/* Flechas solo visibles en Desktop */}
        {!isMobile && showLeftArrow && (
          <IconButton
            onClick={() => scroll('left')}
            sx={{
              position: 'absolute',
              left: -15,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 10,
              backgroundColor: 'white',
              boxShadow: 3,
              '&:hover': { backgroundColor: '#f5f5f5' }
            }}
          >
            <ArrowBackIosNewIcon fontSize="small" />
          </IconButton>
        )}

        <Box
          ref={scrollContainerRef}
          sx={{
            display: 'flex',
            gap: 2,
            overflowX: 'auto', // Permite scroll táctil
            scrollBehavior: 'smooth',
            py: 1,
            px: 1,
            '&::-webkit-scrollbar': { display: 'none' }, 
            msOverflowStyle: 'none', 
            scrollbarWidth: 'none', 
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {iaContentTools.map((tool, i) => (
            <ContentCard key={i} {...tool} />
          ))}
        </Box>

        {!isMobile && showRightArrow && (
          <IconButton
            onClick={() => scroll('right')}
            sx={{
              position: 'absolute',
              right: -15,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 10,
              backgroundColor: 'white',
              boxShadow: 3,
              '&:hover': { backgroundColor: '#f5f5f5' }
            }}
          >
            <ArrowForwardIosIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    </Box>
  );
};

export default CreacionContenidoIA;